// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import type {KeybindCommand, KeyCombo} from '@app/features/input/state/Keybind';
import Keybind from '@app/features/input/state/Keybind';
import {SHIFT_KEY_SYMBOL} from '@app/features/input/utils/KeyboardUtils';
import styles from '@app/features/ui/keybind_hint/KeybindHint.module.css';
import {observer} from 'mobx-react-lite';

const isMac = () => /Mac|iPod|iPhone|iPad/.test(navigator.platform);
interface KeyPart {
	label: string;
	isSymbol?: boolean;
}
const formatKeyParts = (combo: KeyCombo): Array<KeyPart> => {
	const parts: Array<KeyPart> = [];
	const mac = isMac();
	if (combo.ctrl) {
		parts.push(mac ? {label: '⌃', isSymbol: true} : {label: 'Ctrl'});
	} else if (combo.ctrlOrMeta) {
		parts.push(mac ? {label: '⌘', isSymbol: true} : {label: 'Ctrl'});
	}
	if (combo.meta) {
		parts.push(mac ? {label: '⌘', isSymbol: true} : {label: 'Win'});
	}
	if (combo.shift) {
		parts.push({label: SHIFT_KEY_SYMBOL, isSymbol: true});
	}
	if (combo.alt) {
		parts.push(mac ? {label: '⌥', isSymbol: true} : {label: 'Alt'});
	}
	const key = combo.code ?? combo.key ?? '';
	if (key === ' ') {
		parts.push({label: 'Space'});
	} else if (key === 'ArrowUp') {
		parts.push({label: '↑', isSymbol: true});
	} else if (key === 'ArrowDown') {
		parts.push({label: '↓', isSymbol: true});
	} else if (key === 'ArrowLeft') {
		parts.push({label: '←', isSymbol: true});
	} else if (key === 'ArrowRight') {
		parts.push({label: '→', isSymbol: true});
	} else if (key === 'Enter') {
		parts.push(mac ? {label: '↵', isSymbol: true} : {label: 'Enter'});
	} else if (key === 'Escape') {
		parts.push({label: 'Esc'});
	} else if (key === 'Tab') {
		parts.push({label: 'Tab'});
	} else if (key === 'Backspace') {
		parts.push(mac ? {label: '⌫', isSymbol: true} : {label: 'Backspace'});
	} else if (key === 'PageUp') {
		parts.push({label: 'PgUp'});
	} else if (key === 'PageDown') {
		parts.push({label: 'PgDn'});
	} else if (key.length === 1) {
		parts.push({label: key.toUpperCase()});
	} else if (key) {
		parts.push({label: key});
	}
	return parts;
};
export interface KeybindHintProps {
	action?: KeybindCommand;
	combo?: KeyCombo;
}
export const KeybindHint = ({action, combo}: KeybindHintProps) => {
	const resolvedCombo = combo ?? (action ? Keybind.getByAction(action).combo : null);
	if (!resolvedCombo || (!resolvedCombo.key && !resolvedCombo.code)) {
		return null;
	}
	const parts = formatKeyParts(resolvedCombo);
	if (parts.length === 0) {
		return null;
	}
	return (
		<span className={styles.keybindHint}>
			{parts.map((part, index) => (
				<kbd key={index} className={part.isSymbol ? styles.keySymbol : styles.key}>
					{part.label}
				</kbd>
			))}
		</span>
	);
};
export interface TooltipWithKeybindProps {
	label: string;
	action?: KeybindCommand;
	combo?: KeyCombo;
}
export const TooltipWithKeybind = observer(({label, action, combo}: TooltipWithKeybindProps) => {
	const resolvedCombo = combo ?? (action ? Keybind.getByAction(action).combo : null);
	const hasKeybind = resolvedCombo && (resolvedCombo.key || resolvedCombo.code);
	const shouldShowKeybind = hasKeybind && !Accessibility.hideKeyboardHints;
	return (
		<div className={styles.tooltipContent}>
			<span className={styles.label}>{label}</span>
			{shouldShowKeybind && <KeybindHint combo={resolvedCombo} />}
		</div>
	);
});
